/**
 * Runtime switch for the AI Design Assistant (the agent-testing chat).
 *
 * Read once at bootstrap from the environment's appconfig file:
 *   "aiDesignAssistant": { "enabled": true, "apiUrl": "https://agent-dev.cablepro.elek.com" }
 * Absent or not exactly `enabled: true` means OFF: no side-menu item, the
 * ai-design route does not match (so its lazy chunk never loads) and every AI
 * service call is refused before it reaches the network.
 *
 * Pure TypeScript (no Angular) so it is unit-testable under plain Node.
 */

export interface AiDesignAssistantConfig {
  enabled: boolean;
  /** Base URL of the AICPW agent host; empty to use the local-development fallback. */
  apiUrl: string;
}

/** Where a direct visit to the ai-design URL lands while the assistant is off. */
export const AI_DESIGN_FALLBACK_ROUTE = '/app/main/projects';

export const AI_DESIGN_OFF_MESSAGE = 'The AI Design Assistant is not enabled in this environment.';

/** Validate the raw appconfig value; anything unexpected reads as off. */
export function parseAiDesignAssistantConfig(raw: any): AiDesignAssistantConfig {
  if (!raw || typeof raw !== 'object') return { enabled: false, apiUrl: '' };
  const apiUrl = typeof raw.apiUrl === 'string' ? raw.apiUrl.trim().replace(/\/+$/, '') : '';
  return { enabled: raw.enabled === true, apiUrl };
}

/** The flag as loaded at bootstrap. Off until AppPreBootstrap configures it. */
export class AiDesignAssistant {
  private static config: AiDesignAssistantConfig = { enabled: false, apiUrl: '' };

  static configure(raw: any): void {
    AiDesignAssistant.config = parseAiDesignAssistantConfig(raw);
  }

  static get enabled(): boolean {
    return AiDesignAssistant.config.enabled;
  }

  static get apiUrl(): string {
    return AiDesignAssistant.config.apiUrl;
  }
}

/**
 * The agent host's base URL: the appconfig value, else (local development only,
 * never a production build) the environment's aicpwApiUrl or localhost:8000.
 */
export function resolveAiApiUrl(
  config: AiDesignAssistantConfig,
  env: { production?: boolean; aicpwApiUrl?: string },
): string {
  if (config.apiUrl) return config.apiUrl;
  if (env.production) return '';
  return env.aicpwApiUrl || 'http://localhost:8000';
}

/** Route decision for the ai-design path: match it, or send the visitor home. */
export function aiDesignRouteDecision(enabled: boolean): true | string {
  return enabled ? true : AI_DESIGN_FALLBACK_ROUTE;
}

/**
 * fetch() for every AI service call: refused without touching the network
 * while the assistant is off.
 */
export function aiFetch(input: string, init?: RequestInit): Promise<Response> {
  if (!AiDesignAssistant.enabled) return Promise.reject(new Error(AI_DESIGN_OFF_MESSAGE));
  return fetch(input, init);
}
